import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { AFFILIATE_FORM_ANIM } from '../../theme/tokens';
import { Icon } from '../Icon/Icon';
import { Backdrop, Dialog, Head, Title, CloseButton, Body, Footer } from './Modal.styles';

export type ModalProps = {
  open: boolean;
  /**
   * Dialog heading. Translate at the call site (`title={t('Publish this article?')}`)
   * so the extractor harvests the literal; the value also names the dialog for
   * assistive tech.
   */
  title: string;
  /** Called on backdrop click, the close button, and Escape. */
  onClose: () => void;
  children: React.ReactNode;
  /** Action row, typically `Button`s; rendered right-aligned under the body. */
  footer?: React.ReactNode;
  className?: string;
};

/**
 * Centred confirmation/dialog surface, portalled to `document.body` over a
 * backdrop dim that is pinned to the MainLayout content card on desktop
 * (`contentAreaOverlay`). Uses the area's keep-mounted exit pattern (see Toast):
 * while `open` flips off, the last content stays rendered through the exit
 * transition, then unmounts. Escape, the close button, and a backdrop click all
 * call `onClose` — the caller owns the open state.
 */
export const Modal = ({ open, title, onClose, children, footer, className }: ModalProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const [shown, setShown] = React.useState(false);
  const [on, setOn] = React.useState(false);
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const exitTimer = React.useRef<number | undefined>(undefined);
  /** The element that opened the dialog; focus returns to it on close. */
  const openerRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (open) {
      // Show / re-show: cancel any pending unmount, mount at the offset state,
      // then flip to resting one frame later so the transition plays. Remember
      // the opener so focus can return to it on close.
      window.clearTimeout(exitTimer.current);
      openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setShown(true);
      const raf = requestAnimationFrame(() => setOn(true));
      return () => cancelAnimationFrame(raf);
    }
    // Hide: animate out, then unmount once the transition is done and hand
    // focus back to the opener.
    setOn(false);
    exitTimer.current = window.setTimeout(() => {
      setShown(false);
      if (openerRef.current?.isConnected) openerRef.current.focus();
      openerRef.current = null;
    }, AFFILIATE_FORM_ANIM.SHEET_MS);
    return undefined;
  }, [open]);

  // Clear a pending exit timer if the component unmounts mid-animation.
  React.useEffect(() => () => window.clearTimeout(exitTimer.current), []);

  // Focus the dialog on open so Escape lands here and SRs announce the name.
  React.useEffect(() => {
    if (open && shown) dialogRef.current?.focus();
  }, [open, shown]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // Focus trap: aria-modal promises the background is inert, so Tab and
      // Shift+Tab cycle within the dialog instead of escaping into the page.
      if (e.key === 'Tab') {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusables = Array.from(
          dialog.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((el) => !el.hasAttribute('disabled'));
        if (focusables.length === 0) {
          e.preventDefault();
          dialog.focus();
          return;
        }
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        const inside = active instanceof HTMLElement && dialog.contains(active);
        if (e.shiftKey && (!inside || active === first)) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && (!inside || active === last)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!shown) return null;

  return createPortal(
    <Backdrop $on={on} onClick={onClose} className={className}>
      <Dialog
        ref={dialogRef}
        $on={on}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <Head>
          <Title text={title} tag="h2" />
          <CloseButton type="button" aria-label={t('Close')} onClick={onClose}>
            <Icon name="close" size={16} />
          </CloseButton>
        </Head>
        <Body>{children}</Body>
        {footer ? <Footer>{footer}</Footer> : null}
      </Dialog>
    </Backdrop>,
    document.body,
  );
};

export default Modal;
